import { generatePublicId } from 'flow/service/publicIds';
import type { SchemaInstance, WorkflowProps } from 'flow/store/engine/types';
import type {
  Snapshot,
  UiEffectContext,
  UiEffectContextByTarget,
  WorkflowDiff,
  WorkflowHistoryEntry,
} from 'flow/store/redux/types';
import type { Delta } from 'jsondiffpatch';
import * as jsondiffpatch from 'jsondiffpatch';
import { historyAction } from './constants';
import type { HistoryAction } from './constants';

export function createSnapshot<T>(args: { data: T }): Snapshot<T> {
  return {
    snapshotId: generatePublicId('snapshot'),
    data: args.data,
  };
}

export function trimSubsequentSnapshots(
  snapshots: Snapshot<unknown>[],
  snapshotId: Snapshot<unknown>['snapshotId'],
) {
  const snapshotIndex = snapshots.findIndex(snapshot => snapshot.snapshotId === snapshotId);
  return snapshotIndex === -1
    ? snapshots.slice()
    : snapshots.slice(0, snapshotIndex + 1);
}

export function isUndo(action: HistoryAction): boolean {
  return action === historyAction.undo;
}

// Match array items by their stable identity (publicId/id) so reorders and
// insertions produce minimal, correct deltas instead of positional ones.
// Items without an identity fall back to index-based matching (the default).
const diffPatcher = jsondiffpatch.create({
  objectHash: (item: unknown, index?: number) => {
    if (item && typeof item === 'object') {
      const { publicId, id } = item as { publicId?: unknown; id?: unknown };
      if (publicId != null) return String(publicId);
      if (id != null) return String(id);
    }
    return `$$index:${index}`;
  },
});

export function computeDiff(initial: unknown, current: unknown): Delta {
  return diffPatcher.diff(initial, current);
}

export function canRedo(
  history: WorkflowHistoryEntry[],
  historyIndex: number,
): boolean {
  return historyIndex < history.length - 1;
}

export function removeRedoStack(
  history: WorkflowHistoryEntry[],
  historyIndex: number,
): WorkflowHistoryEntry[] {
  return history.slice(0, historyIndex + 1);
}

export function diffWorkflowState(
  initial: {
    workflowData: Partial<SchemaInstance>;
    workflowProps: WorkflowProps;
  },
  current: {
    workflowData: Partial<SchemaInstance>;
    workflowProps: WorkflowProps;
  },
): WorkflowDiff {
  return {
    workflowData: computeDiff(initial.workflowData, current.workflowData),
    workflowProps: computeDiff(initial.workflowProps, current.workflowProps),
  };
}

/**
 * Builds a `UiEffectContext` for a given `target`, returning the context that matches it.
 * `args` are the context's payload fields (everything except the `target` discriminant), so
 * each target is enforced to receive exactly the ids it needs.
 *
 * @example
 *   createUiEffectContext('action', { publicId });
 *   createUiEffectContext('threshold', { publicId, actionId });
 */
export function createUiEffectContext<TTarget extends UiEffectContext['target']>(
  target: TTarget,
  args: Omit<UiEffectContextByTarget[TTarget], 'target'>,
): UiEffectContextByTarget[TTarget] {
  return { ...args, target } as UiEffectContextByTarget[TTarget];
}
